import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    FaArrowLeft,
    FaCalendarAlt,
    FaClock,
    FaMapMarkerAlt,
    FaUser,
    FaBuilding,
    FaLocationArrow,
    FaCheckCircle,
    FaSignInAlt,
    FaSignOutAlt,
} from "react-icons/fa";
import { formatDate, formatDuration, formatTime } from "../../../utils/date";
import { capitalizeWords } from "../../../utils/string";
import FullPageSpinner from "../../../components/FullPageSpinner";

const AttendanceDetails = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [attendance, setAttendance] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAttendance = async () => {
            setLoading(true);
            try {

                const attStr = localStorage.getItem("attendance");
                const att = JSON.parse(attStr ?? "{}");

                // console.log(att)
                setAttendance(att)
                setAttendance({
                    ...att,
                    id: att.id,
                    clockIn: formatTime(att.clockIn),
                    clockOut: att.clockOut ? formatTime(att.clockOut) : "---",
                    hoursWorked: att.clockOut
                        ? formatDuration(att.clockIn, att.clockOut) : "Ongoing",
                    distance: att.distance,
                    date: formatDate(att.createdAt),
                    siteAddress: att.siteAddress,
                });
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchAttendance();
    }, [id]);

    if (loading) {
        return (
            <div className="flex min-h-[70vh] items-center justify-center">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-indigo-600" />
            </div>
        );
    }

    if (!attendance) {
        return (
            <div className="flex min-h-[70vh] flex-col items-center justify-center px-4">
                <div className="mb-4 rounded-full bg-gray-100 p-5 text-gray-400">
                    {FaUser({ size: 28 })}
                </div>

                <h2 className="text-xl font-semibold text-gray-800">
                    Attendance not found
                </h2>

                <p className="mt-2 text-sm text-gray-500">
                    The attendance record you're looking for doesn't exist.
                </p>

                <button
                    onClick={() => navigate(-1)}
                    className="mt-6 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700"
                >
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-6 sm:px-6 lg:px-8">

            {/* Header */}
            <div className="mx-auto max-w-7xl">
                <FullPageSpinner show={loading} />

                <button
                    // onClick={() => navigate("/attendance-logs")}
                    onClick={() => navigate(-1)}
                    className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-gray-600 transition hover:text-indigo-600"
                >
                    {FaArrowLeft({ size: 14 })}
                    Back to Attendance
                </button>

                {/* Profile Header */}
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">

                    <div className="p-5 sm:p-7">

                        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">

                            <div className="flex items-center gap-4">

                                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-lg font-bold text-indigo-600">
                                    {attendance.employeeName
                                        ?.split(" ")
                                        .map((name: string) => name[0])
                                        .join("")
                                        .slice(0, 2)}
                                </div>

                                <div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h1 className="text-xl font-bold text-gray-900 sm:text-2xl capitalize">
                                            {attendance.employeeName}
                                        </h1>

                                        <span className={
                                            `inline-flex items-center gap-1.5 rounded-full
                                             bg-${attendance.clockOut !== "---" ? "green" : "red"}-50 px-3 
                                             py-1 text-xs font-semibold text-${attendance.clockOut !== "---" ? "green" : "red"}-700`}>
                                            <span className={`h-1.5 w-1.5 rounded-full
                                                 bg-${attendance.clockOut !== "---" ? "green" : "red"}-500`} />
                                            {attendance.clockOut !== "---" ? "Completed" : "Ongoing"}
                                        </span>
                                    </div>

                                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                                        <span>{attendance.employeeId}</span>
                                        <span className="hidden sm:block">•</span>
                                        <span>{attendance.department}</span>
                                    </div>
                                </div>

                            </div>

                            <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-4 py-2.5 text-sm text-gray-600">
                                {FaCalendarAlt({ className: "text-indigo-500" })}
                                {attendance.date}
                            </div>

                        </div>

                    </div>

                </div>

                {/* Summary Cards */}
                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">

                    {/* Clock In */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">
                                    Clock In
                                </p>

                                <p className="mt-2 text-2xl font-bold text-gray-900">
                                    {attendance.clockIn}
                                </p>
                            </div>

                            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-green-50 text-green-600">
                                {FaSignInAlt({})}
                            </div>
                        </div>

                        <p className="mt-3 text-xs text-gray-400">
                            Start of attendance
                        </p>
                    </div>

                    {/* Clock Out */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">
                                    Clock Out
                                </p>

                                <p className="mt-2 text-2xl font-bold text-gray-900">
                                    {attendance.clockOut}
                                </p>
                            </div>

                            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-red-500">
                                {FaSignOutAlt({})}
                            </div>
                        </div>

                        <p className="mt-3 text-xs text-gray-400">
                            End of attendance
                        </p>
                    </div>

                    {/* Hours */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">
                                    Hours Worked
                                </p>

                                <p className="mt-2 text-2xl font-bold text-gray-900">
                                    {attendance.hoursWorked}
                                </p>
                            </div>

                            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                                {FaClock({})}
                            </div>
                        </div>

                        <p className="mt-3 text-xs text-gray-400">
                            Total working duration
                        </p>
                    </div>

                    {/* Distance */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">
                                    Distance
                                </p>

                                <p className="mt-2 text-2xl font-bold text-gray-900">
                                    {attendance.distance}
                                </p>
                            </div>

                            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                                {FaLocationArrow({})}
                            </div>
                        </div>

                        <p className="mt-3 text-xs text-gray-400">
                            From assigned site
                        </p>
                    </div>

                </div>

                {/* Main Content */}
                <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">

                    {/* Attendance Information */}
                    <div className="lg:col-span-2">

                        <div className="rounded-2xl border border-gray-200 bg-white">

                            <div className="border-b border-gray-100 px-5 py-5 sm:px-6">
                                <h2 className="font-semibold text-gray-900">
                                    Attendance Information
                                </h2>

                                <p className="mt-1 text-sm text-gray-500">
                                    Detailed information about this attendance record.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 gap-x-8 gap-y-6 p-5 sm:grid-cols-2 sm:p-6">

                                <InfoItem
                                    icon={FaUser({})}
                                        // <ProfileAvatar
                                        //     photoUrl={attendance.photoUrl}
                                        //     firstName={attendance.employeeName}
                                        //     size="w-9 h-9"
                                        //     className="border-2 border-white shadow-sm"
                                        // />
                                    label="Staff"
                                    value={attendance.employeeName}
                                />

                                <InfoItem
                                    icon={FaBuilding({})}
                                    label="Department"
                                    value={capitalizeWords(attendance.department)}
                                />

                                <InfoItem
                                    icon={FaCalendarAlt({})}
                                    label="Attendance Date"
                                    value={attendance.date}
                                />

                                <InfoItem
                                    icon={FaMapMarkerAlt({})}
                                    label="Assigned Site"
                                    value={capitalizeWords(attendance.site)}
                                />

                                <InfoItem
                                    icon={FaSignInAlt({})}
                                    label="Clock In"
                                    value={attendance.clockIn}
                                />

                                <InfoItem
                                    icon={FaSignOutAlt({})}
                                    label="Clock Out"
                                    value={attendance.clockOut || "---"}
                                />
                            </div>
                        </div>

                    </div>

                    {/* Site Information */}
                    <div>

                        <div className="rounded-2xl border border-gray-200 bg-white">

                            <div className="border-b border-gray-100 px-5 py-5">
                                <h2 className="font-semibold text-gray-900">
                                    Site Information
                                </h2>

                                <p className="mt-1 text-sm text-gray-500">
                                    Assigned work location.
                                </p>
                            </div>

                            <div className="p-5">

                                <div className="flex h-40 items-center justify-center rounded-xl bg-gray-100">
                                    <div className="text-center">
                                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white text-indigo-600 shadow-sm">
                                            {FaMapMarkerAlt({})}
                                        </div>

                                        <p className="mt-3 text-sm font-medium text-gray-700 capitalize">
                                            {attendance.site}
                                        </p>

                                        <p className="mt-1 text-xs text-gray-500">
                                            Assigned location
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-5">

                                    <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                                        Site
                                    </p>

                                    <p className="mt-1 font-semibold text-gray-900 capitalize">
                                        {attendance.site}
                                    </p>

                                </div>

                                <div className="mt-5 border-t border-gray-100 pt-5">

                                    <p className="text-xs font-medium uppercase tracking-wide text-gray-400 capitalize">
                                        Address
                                    </p>

                                    <p className="mt-1 text-sm leading-6 text-gray-600 capitalize">
                                        {attendance.siteAddress}
                                    </p>

                                </div>

                                <button
                                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                                >
                                    {FaMapMarkerAlt({ className: "text-indigo-600" })}
                                    View on Map
                                </button>

                            </div>

                        </div>

                    </div>

                </div>

            </div>
        </div>
    );
};

const InfoItem = ({
    icon,
    label,
    value,
}: {
    icon: React.ReactNode;
    label: string;
    value: string;
}) => {
    return (
        <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-sm text-gray-500">
                {icon}
            </div>

            <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                    {label}
                </p>

                <p className="mt-1 truncate font-medium text-gray-800">
                    {value}
                </p>
            </div>
        </div>
    );
};

export default AttendanceDetails;